import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, FileStack, Star, GitBranch, ListTree } from 'lucide-react';
import { Button, Modal, Input, Loading, EmptyState, Badge } from '@/components/ui';
import { formatDate } from '@/utils/date';
import LineEditor, { DURATION_TYPE_UNSET, hasUnsetDuration } from '@/components/affiliate/LineEditor';
import {
  affiliateApi,
  todayISODate,
  REWARD_TYPES,
  PAYOUT_MECHANISMS,
  DURATION_TYPES,
  type DurationType,
  type LineInput,
  type PayoutMechanism,
  type RewardType,
  type RewardTemplateWithLines,
  type TemplateHeaderInput,
} from '@/api/affiliate';

export default function TemplatesPage() {
  const qc = useQueryClient();
  const [headerModal, setHeaderModal] = useState<{ open: boolean; base?: RewardTemplateWithLines }>({
    open: false,
  });
  const [linesModal, setLinesModal] = useState<{ open: boolean; template?: RewardTemplateWithLines }>({
    open: false,
  });

  const { data: templates, isLoading } = useQuery({
    queryKey: ['affiliate-templates'],
    queryFn: () => affiliateApi.templates.list(),
  });

  const setDefaultMut = useMutation({
    mutationFn: (id: string) => affiliateApi.templates.setDefault(id),
    onSuccess: () => {
      toast.success('Default template updated');
      qc.invalidateQueries({ queryKey: ['affiliate-templates'] });
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileStack className="h-5 w-5 text-indigo-400" />
          <div>
            <h1 className="text-xl font-semibold text-white">Reward Templates</h1>
            <p className="text-xs text-ink-muted mt-0.5">
              Versioned default contracts used to seed partner agreements
            </p>
          </div>
        </div>
        <Button onClick={() => setHeaderModal({ open: true })}>
          <Plus size={16} className="mr-1" /> New template
        </Button>
      </div>

      {isLoading ? (
        <Loading text="Loading templates…" />
      ) : !(templates ?? []).length ? (
        <EmptyState
          icon={<FileStack className="h-8 w-8" />}
          title="No templates yet"
          description="Create a default template (e.g. 10% while paying) to seed partner agreements."
          actionLabel="New template"
          onAction={() => setHeaderModal({ open: true })}
        />
      ) : (
        <div className="space-y-3">
          {templates!.map((t) => (
            <div key={t.id} className="card p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-white font-medium">{t.name}</span>
                <Badge variant="neutral">v{t.version}</Badge>
                {t.is_default && (
                  <Badge variant="success">
                    <Star size={11} className="mr-1" /> default
                  </Badge>
                )}
                {!t.is_active && <Badge variant="neutral">inactive</Badge>}
                <span className="ms-auto text-xs text-ink-muted capitalize">
                  {t.reward_type.replace(/_/g, ' ')} · {t.payout_mechanism.replace(/_/g, ' ')} · from{' '}
                  {formatDate(t.effective_from)}
                </span>
              </div>

              <div className="text-sm text-ink-secondary">
                Default: <span className="num text-ink-primary">{t.commission_pct}%</span> · min{' '}
                <span className="num">{t.min_active_subs}</span> subs ·{' '}
                {t.duration_type === 'fixed' ? `${t.duration_months} mo` : 'while paying'} ·{' '}
                <span className="num">{t.lines.length}</span> product line(s)
              </div>

              <div className="flex flex-wrap gap-2">
                {!t.is_default && (
                  <Button size="sm" variant="secondary" onClick={() => setDefaultMut.mutate(t.id)} loading={setDefaultMut.isPending}>
                    <Star size={14} className="mr-1" /> Set default
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => setLinesModal({ open: true, template: t })}>
                  <ListTree size={14} className="mr-1" /> Edit lines
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setHeaderModal({ open: true, base: t })}>
                  <GitBranch size={14} className="mr-1" /> New version
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplateHeaderModal
        open={headerModal.open}
        base={headerModal.base}
        onClose={() => setHeaderModal({ open: false })}
      />
      <EditLinesModal
        open={linesModal.open}
        template={linesModal.template}
        onClose={() => setLinesModal({ open: false })}
      />
    </div>
  );
}

function TemplateHeaderModal({
  open,
  base,
  onClose,
}: {
  open: boolean;
  base?: RewardTemplateWithLines;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isNewVersion = !!base;

  const [name, setName] = useState('');
  const [rewardType, setRewardType] = useState<RewardType>('cash_payout');
  const [payoutMechanism, setPayoutMechanism] = useState<PayoutMechanism>('bank_transfer');
  const [commissionPct, setCommissionPct] = useState(10);
  const [minActiveSubs, setMinActiveSubs] = useState(0);
  const [durationType, setDurationType] = useState<DurationType>(DURATION_TYPE_UNSET);
  const [durationMonths, setDurationMonths] = useState<number | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState(todayISODate());
  const [lines, setLines] = useState<LineInput[]>([]);

  const { data: products } = useQuery({
    queryKey: ['affiliate-products'],
    queryFn: () => affiliateApi.catalog.productOptions(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setName(base?.name ?? '');
    setRewardType(base?.reward_type ?? 'cash_payout');
    setPayoutMechanism(base?.payout_mechanism ?? 'bank_transfer');
    setCommissionPct(base?.commission_pct ?? 10);
    setMinActiveSubs(base?.min_active_subs ?? 0);
    setDurationType(base?.duration_type ?? DURATION_TYPE_UNSET);
    setDurationMonths(base?.duration_months ?? null);
    setIsDefault(base?.is_default ?? false);
    setEffectiveFrom(todayISODate());
    setLines(
      (base?.lines ?? []).map((l) => ({
        system_code: l.system_code,
        product_code: l.product_code,
        commission_pct: l.commission_pct,
        min_active_subs: l.min_active_subs,
        duration_type: l.duration_type,
        duration_months: l.duration_months,
      })),
    );
  }, [open, base]);

  const saveMut = useMutation({
    mutationFn: () => {
      const header: TemplateHeaderInput = {
        name,
        reward_type: rewardType,
        commission_pct: commissionPct,
        min_active_subs: minActiveSubs,
        duration_type: durationType,
        duration_months: durationType === 'fixed' ? durationMonths : null,
        payout_mechanism: payoutMechanism,
        is_default: isDefault,
        effective_from: effectiveFrom,
      };
      return affiliateApi.templates.createVersion(header, lines, base?.id ?? null);
    },
    onSuccess: () => {
      toast.success(isNewVersion ? 'New version created' : 'Template created');
      qc.invalidateQueries({ queryKey: ['affiliate-templates'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  return (
    <Modal isOpen={open} onClose={onClose} title={isNewVersion ? `New version of "${base?.name}"` : 'New template'} size="lg">
      <div className="space-y-4">
        <Input label="Template name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Standard partner" required />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Effective from">
            <input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
            />
          </Field>
          <Field label="Reward type">
            <select
              value={rewardType}
              onChange={(e) => setRewardType(e.target.value as RewardType)}
              className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
            >
              {REWARD_TYPES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Payout mechanism">
            <select
              value={payoutMechanism}
              onChange={(e) => setPayoutMechanism(e.target.value as PayoutMechanism)}
              className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
            >
              {PAYOUT_MECHANISMS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Default %">
            <input
              type="number"
              min={0}
              max={100}
              value={commissionPct}
              onChange={(e) => setCommissionPct(Number(e.target.value))}
              className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm num"
            />
          </Field>
          <Field label="Min active subs">
            <input
              type="number"
              min={0}
              value={minActiveSubs}
              onChange={(e) => setMinActiveSubs(Number(e.target.value))}
              className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm num"
            />
          </Field>
          <Field label="Duration">
            <select
              value={durationType || ''}
              onChange={(e) => {
                const dt = e.target.value as DurationType;
                setDurationType(dt);
                setDurationMonths(dt === 'fixed' ? durationMonths ?? 12 : null);
              }}
              className={`w-full h-10 px-3 rounded border bg-navy-deeper text-ink-primary text-sm ${
                durationType ? 'border-navy-border' : 'border-danger'
              }`}
            >
              <option value="" disabled>
                — choose —
              </option>
              {DURATION_TYPES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </Field>
          {durationType === 'fixed' && (
            <Field label="Months">
              <input
                type="number"
                min={1}
                value={durationMonths ?? 12}
                onChange={(e) => setDurationMonths(Number(e.target.value))}
                className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm num"
              />
            </Field>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-ink-secondary">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Make this the default template (supersedes the current default)
        </label>

        <LineEditor lines={lines} products={products ?? []} onChange={setLines} />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!name.trim()) return toast.error('Name is required');
              if (!durationType) return toast.error('Duration must be set — choose fixed or unlimited');
              if (durationType === 'fixed' && (!durationMonths || durationMonths < 1))
                return toast.error('Fixed duration requires months');
              if (hasUnsetDuration(lines))
                return toast.error('Every product line needs a duration — choose fixed or unlimited');
              saveMut.mutate();
            }}
            loading={saveMut.isPending}
          >
            {isNewVersion ? 'Create version' : 'Create template'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function EditLinesModal({
  open,
  template,
  onClose,
}: {
  open: boolean;
  template?: RewardTemplateWithLines;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [lines, setLines] = useState<LineInput[]>([]);

  const { data: products } = useQuery({
    queryKey: ['affiliate-products'],
    queryFn: () => affiliateApi.catalog.productOptions(),
    enabled: open,
  });

  useEffect(() => {
    if (!open || !template) return;
    setLines(
      template.lines.map((l) => ({
        system_code: l.system_code,
        product_code: l.product_code,
        commission_pct: l.commission_pct,
        min_active_subs: l.min_active_subs,
        duration_type: l.duration_type,
        duration_months: l.duration_months,
      })),
    );
  }, [open, template]);

  const saveMut = useMutation({
    mutationFn: () => affiliateApi.templates.replaceLines(template!.id, lines),
    onSuccess: () => {
      toast.success('Lines updated');
      qc.invalidateQueries({ queryKey: ['affiliate-templates'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  return (
    <Modal isOpen={open} onClose={onClose} title={`Edit lines — ${template?.name ?? ''}`} size="lg">
      <div className="space-y-4">
        <LineEditor lines={lines} products={products ?? []} onChange={setLines} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (hasUnsetDuration(lines))
                return toast.error('Every product line needs a duration — choose fixed or unlimited');
              saveMut.mutate();
            }}
            loading={saveMut.isPending}
          >
            Save lines
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink-secondary mb-1.5">{label}</label>
      {children}
    </div>
  );
}
